/**
 * Multi-Cart API Service
 *
 * API methods for fetching multi-cart data from Core Backend.
 */

import { coreAPI } from '@/lib/api/client';
import type { MultiCartResponse, CartItemDetail } from './types';

/**
 * Fetch multi-cart data for the current user
 *
 * @param userCurrency - User's preferred currency code (e.g., 'USD', 'ARS')
 * @returns Promise<MultiCartResponse> - Aggregated cart data from all merchants
 * @throws ApiError on failure
 */
export async function getMultiCart(userCurrency?: string): Promise<MultiCartResponse> {
  const searchParams = userCurrency ? `?currency=${userCurrency}` : '';
  return coreAPI.get<MultiCartResponse>(`/users/me/carts${searchParams}`);
}

/**
 * Request body for adding an item to a merchant cart
 */
export interface AddCartItemRequest {
  productGuid: string;
  /** Whole number >= 1 */
  quantity: number;
}

/**
 * Updated merchant cart summary returned by the add-to-cart endpoint (CF-PCARD-1.1).
 * Matches POST /api/v1/users/me/carts/{merchantGuid}/items response payload.
 */
export interface AddCartItemResult {
  merchantGuid: string;
  itemCount: number;
  subtotal: number;
  currency: string;
  items?: CartItemDetail[];
}

/**
 * Add a single product to a specific merchant's cart.
 *
 * Proxies through the Core Backend read-merge-sync endpoint (CF-PCARD-1.1) so the
 * front end never builds per-merchant Storefront URLs (merchant isolation).
 *
 * @param merchantGuid - Target merchant (UUID)
 * @param body - productGuid (UUID) + quantity (>= 1)
 * @returns Promise<AddCartItemResult> - updated merchant cart summary
 * @throws ApiError - code `CART_ITEM_UNAVAILABLE` (out of stock / not found),
 *   `MERCHANT_UNAVAILABLE`, or `MERCHANT_NOT_FOUND`
 */
export async function addCartItem(
  merchantGuid: string,
  body: AddCartItemRequest
): Promise<AddCartItemResult> {
  return coreAPI.post<AddCartItemResult>(
    `/users/me/carts/${merchantGuid}/items`,
    body
  );
}
